import {FC, useEffect, useState} from 'react'
import {Button, Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import SharedData from '../../common/SharedData'
import Loader from '../../common/Loader'
import ProductThumb from '../../../components/common/ProductThumb'
import {toCatalogImageUrl} from '../../../../_metronic/helpers'
import {getStockRequest} from '../core/_requests'
import {IStockRequestDetails} from '../core/_models'
import {directionClasses} from '../core/direction'
import {extractErrorMessage} from '../core/errors'
import StockRequestStatus from './StockRequestStatus'

type Props = {
  requestId: number | null
  onClose: () => void
}

const StockRequestDetailsModal: FC<Props> = ({requestId, onClose}) => {
  const {formatMessage} = useIntl()
  const {currentUser, language} = SharedData()
  const dir = directionClasses(language)
  const token = currentUser?.token
  const [details, setDetails] = useState<IStockRequestDetails | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (requestId === null) return
    let active = true
    setDetails(null)
    setError(null)
    setLoading(true)
    getStockRequest(requestId, token, language)
      .then((res) => {
        if (active) setDetails(res.data?.data ?? null)
      })
      .catch((err) => {
        if (active)
          setError(extractErrorMessage(err) || formatMessage({id: 'something_wrong_desc'}))
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [requestId, token, language, formatMessage])

  const items = details?.items ?? []

  return (
    <Modal show={requestId !== null} onHide={onClose} size='lg' centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          {formatMessage({id: 'add_to_stock.details.title'}, {id: requestId ?? ''})}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <Loader />
        ) : error ? (
          <div className='text-danger text-center py-5'>{error}</div>
        ) : details ? (
          <>
            <div className='d-flex flex-wrap gap-8 mb-6'>
              <div>
                <div className='text-muted fs-7'>{formatMessage({id: 'date_added'})}</div>
                <div className='fw-semibold'>{details.date_added}</div>
              </div>
              <div>
                <div className='text-muted fs-7'>{formatMessage({id: 'status'})}</div>
                <StockRequestStatus request={details} />
              </div>
              {details.decided_at && (
                <div>
                  <div className='text-muted fs-7'>
                    {formatMessage({id: 'add_to_stock.decided_at'})}
                  </div>
                  <div className='fw-semibold'>{details.decided_at}</div>
                </div>
              )}
              <div>
                <div className='text-muted fs-7'>
                  {formatMessage({id: 'add_to_stock.total_quantity'})}
                </div>
                <div className='fw-semibold'>{details.total_quantity}</div>
              </div>
            </div>
            {items.length === 0 ? (
              <div className='text-muted text-center py-5'>{formatMessage({id: 'no_data'})}</div>
            ) : (
              <div className='table-responsive'>
                <table className='table table-row-bordered table-row-gray-200 align-middle gy-3'>
                  <thead>
                    <tr className='fw-bold text-muted bg-light'>
                      <th className={`${dir.padStart} ${dir.roundStart} w-100px text-center`}>
                        {formatMessage({id: 'image'})}
                      </th>
                      <th>{formatMessage({id: 'prod_name'})}</th>
                      <th>{formatMessage({id: 'barcode'})}</th>
                      <th>{formatMessage({id: 'model'})}</th>
                      <th className={`${dir.end} ${dir.padEnd} ${dir.roundEnd}`}>
                        {formatMessage({id: 'quantity'})}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={index}>
                        <td className={`${dir.padStart} text-center`}>
                          <ProductThumb
                            src={toCatalogImageUrl(item.image)}
                            alt={item.name ?? ''}
                            loading='lazy'
                          />
                        </td>
                        <td className={`fw-semibold ${dir.start}`}>
                          {item.name ?? `#${item.product_id}`}
                        </td>
                        <td>{item.barcode || '—'}</td>
                        <td>{item.model || '—'}</td>
                        <td className={`${dir.end} ${dir.padEnd} fw-bold`}>{item.quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='light' onClick={onClose}>
          {formatMessage({id: 'close'})}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default StockRequestDetailsModal
